import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Plus, Upload, Loader2 } from "lucide-react";
import { ownerApi, ownerUpload, fileUrl } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common/States";
import { Switch } from "@/components/ui/switch";

export default function OwnerBanners() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["ownerBanners"], queryFn: ownerApi.getBanners });
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ownerBanners"] });

  const create = useMutation({
    mutationFn: () => ownerApi.createBanner({ image_url: form.image_url, headline: form.headline, detail: form.detail, active: true }),
    onSuccess: () => { invalidate(); setForm(null); toast.success("Banner added"); },
    onError: (e) => toast.error(e.message),
  });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await ownerUpload(file);
      setForm((f) => ({ ...f, image_url: res.url || fileUrl(res.path) }));
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (query.isPending) return <Spinner />;

  return (
    <div className="space-y-4">
      <button onClick={() => setForm({ image_url: "", headline: "", detail: "" })} className="inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> New banner</button>
      {!query.data?.length ? <EmptyState title="No banners" hint="Add promotional banners for the home carousel." /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {query.data.map((b) => (
            <div key={b.id} className="overflow-hidden rounded-2xl border border-border bg-card">
              {b.image_url ? <img src={b.image_url} alt={b.headline} className="h-32 w-full object-cover" /> : null}
              <div className="flex items-center justify-between p-3">
                <div><p className="text-sm font-semibold">{b.headline}</p><p className="text-xs text-muted-foreground">{b.detail}</p></div>
                <div className="flex items-center gap-2">
                  <Switch checked={b.active} onCheckedChange={(v) => ownerApi.updateBanner(b.id, { active: v }).then(invalidate)} />
                  <button onClick={() => ownerApi.deleteBanner(b.id).then(invalidate)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {form ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForm(null)}>
          <div className="w-full max-w-sm space-y-3 rounded-2xl bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold">New banner</h3>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-primary px-4 py-2 text-sm font-semibold text-primary">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload image
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
            </label>
            {form.image_url ? <img src={form.image_url} alt="" className="h-28 w-full rounded-lg object-cover" /> : null}
            <input placeholder="Headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <input placeholder="Detail" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} className="w-full rounded-xl border border-border p-2.5 text-sm" />
            <button onClick={() => create.mutate()} disabled={!form.image_url || create.isPending} className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">Add banner</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
